import { HStack, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { Copy } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

interface CopyToClipboardProps {
  /** The value copied to the clipboard. When empty/nullish, children render with no copy control. */
  value: string | null | undefined;
  /** Used for both the icon button's aria-label and the tooltip. Pass an already-translated string. */
  label?: string;
  /** The content to display (text, link, badge, …). Keeps its own behaviour. */
  children: React.ReactNode;
  /** Title for the success toast (default: "Copied to clipboard"). */
  toastTitle?: string;
  /** Reveal the copy control only on hover/focus (desktop); stays visible on touch. */
  revealOnHover?: boolean;
}

export function CopyToClipboard({
  value,
  label = 'Copy to clipboard',
  children,
  toastTitle = 'Copied to clipboard',
  revealOnHover = false,
}: CopyToClipboardProps) {
  const toast = useToast();

  if (value == null || value === '') {
    return children;
  }

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(value);

    toast({
      title: toastTitle,
      status: 'success',
      duration: 3000,
    });
  };

  return (
    <HStack spacing={1} role={revealOnHover ? 'group' : undefined}>
      {children}
      <Tooltip label={label} fontSize="xs">
        <IconButton
          aria-label={label}
          icon={<Copy size={iconSize.sm} />}
          size="xs"
          variant="ghost"
          minW="auto"
          color="fg.muted"
          onClick={handleCopy}
          opacity={revealOnHover ? 0 : 1}
          transition="opacity 0.15s ease"
          _hover={{ color: 'fg.default' }}
          _groupHover={{ opacity: 1 }}
          _focusVisible={{ opacity: 1 }}
          sx={revealOnHover ? { '@media (hover: none)': { opacity: 1 } } : undefined}
        />
      </Tooltip>
    </HStack>
  );
}
